/**
 * Hero Section — animated landing hero (HandiPrêt)
 *
 * Features:
 * - Animated gradient blobs (decorative)
 * - gradient-text on title
 * - Staggered Framer Motion entrance animations
 * - Stats row
 * - CTA buttons with pulse-glow
 */

"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { ArrowRight, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";

export function HeroSection() {
    return (
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-32 pb-20 bg-gradient-to-br from-hp-purple/10 via-white to-hp-orange/10 dark:from-hp-purple/5 dark:via-background dark:to-hp-orange/5">
            {/* Animated background blobs — hidden on mobile */}
            <motion.div
                className="absolute top-20 -left-32 h-96 w-96 rounded-full bg-hp-purple/20 blur-3xl hidden md:block"
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute bottom-20 -right-32 h-80 w-80 rounded-full bg-hp-orange/20 blur-3xl hidden md:block"
                animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.6, 0.4] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-64 rounded-full bg-hp-teal/10 blur-3xl hidden md:block"
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="container mx-auto px-4">
                <div className="grid gap-12 items-center lg:grid-cols-2">
                    {/* Text content */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                    >
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1, duration: 0.5 }}
                        >
                            <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-hp-purple to-hp-orange px-4 py-1.5 text-sm font-bold text-white">
                                <Heart className="h-3 w-3" aria-hidden="true" />
                                Initiative solidaire 100% gratuite
                            </span>
                        </motion.div>

                        {/* Heading */}
                        <motion.h1
                            className="mt-6 text-5xl font-black leading-tight md:text-7xl"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                        >
                            Matériel solidaire
                            <br />
                            pour{" "}
                            <span className="gradient-text">enfants</span>
                        </motion.h1>

                        {/* Subtitle */}
                        <motion.p
                            className="mt-6 text-xl text-hp-gray max-w-lg"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                        >
                            Prêt gratuit de matériel médical et paramédical pour les enfants
                            en situation de handicap. Porté par l&apos;Association LEA.
                        </motion.p>

                        {/* CTA buttons */}
                        <motion.div
                            className="mt-10 flex flex-col gap-4 sm:flex-row"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.5 }}
                        >
                            <Button
                                asChild
                                size="lg"
                                className="bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp hover:from-hp-purple-dark hover:to-hp-orange-dark hover:shadow-hp-hover animate-pulse-glow rounded-xl"
                            >
                                <Link href="/annonces">
                                    Trouver du matériel
                                    <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                                </Link>
                            </Button>
                            <Button
                                asChild
                                size="lg"
                                variant="outline"
                                className="border-2 border-hp-purple text-hp-purple hover:bg-hp-purple hover:text-white rounded-xl transition-all"
                            >
                                <Link href="/annonces/nouvelle">Proposer du matériel</Link>
                            </Button>
                        </motion.div>

                        {/* Stats */}
                        <motion.div
                            className="mt-12 grid grid-cols-3 gap-6 border-t border-gray-200 dark:border-gray-700 pt-8"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                        >
                            {[
                                { number: "100%", label: "Gratuit" },
                                { number: "50+", label: "Matériels" },
                                { number: "LEA", label: "Association" },
                            ].map((stat) => (
                                <div key={stat.label}>
                                    <p className="text-3xl font-black gradient-text md:text-4xl">
                                        {stat.number}
                                    </p>
                                    <p className="text-sm text-hp-gray-light">{stat.label}</p>
                                </div>
                            ))}
                        </motion.div>
                    </motion.div>

                    {/* Logo / visual */}
                    <motion.div
                        className="relative flex items-center justify-center"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4, duration: 0.8 }}
                    >
                        <div className="relative">
                            {/* Decorative teal circles (like handipret.fr) */}
                            <motion.div
                                className="absolute -top-8 -left-8 h-64 w-64 rounded-full border-4 border-hp-teal/30 hidden md:block"
                                animate={{ y: [0, -20, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            />
                            <motion.div
                                className="absolute -bottom-8 -right-8 h-48 w-48 rounded-full border-4 border-hp-orange/30 hidden md:block"
                                animate={{ y: [0, 20, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            />

                            {/* Logo large */}
                            <Image
                                src="/logo-handipret.png"
                                alt="HandiPrêt — Matériel solidaire pour enfants"
                                width={500}
                                height={353}
                                style={{ height: 'auto' }}
                                className="relative z-10 drop-shadow-2xl"
                                priority
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
